import { IconPencil } from '@tabler/icons-react'
import { FC, useCallback, useState } from 'react'
import { api } from 'src/api/root'
import { roles } from 'src/enviroment/constants/constants'
import { PrimaryButton } from '../../atoms/buttons/PrimaryButton'
import { SecondaryButton } from '../../atoms/buttons/SecondaryButton'
import { Input } from '../../atoms/inputs/Input'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../atoms/accordion'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../atoms/dialog'
import { Label } from '../../atoms/label'
import { RadioGroup, RadioGroupItem } from '../../atoms/radio-group'
import { Checkbox } from '../../atoms/buttons/CheckBoxButton'
import { z } from 'zod'
import { SubmitHandler, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Member } from 'src/templates/members/columns'
import Typography from '../../atoms/Typography'
import { firstName, lastName } from 'src/enviroment/validation/fields'
import { GradientBorderButton } from '../../atoms/buttons/GradientBorderButton'
import { UserRole } from '../../../types/user'

const EditMemberSchema = z.object({
  firstName: firstName,
  lastName: lastName,
  role: z.nativeEnum(UserRole),
  projects: z.array(z.string().uuid()),
})

type EditMemberFormData = z.infer<typeof EditMemberSchema>

interface Props {
  member: Member
}

const EditMemberDialog: FC<Props> = ({ member }) => {
  const initialProjectIds = member.tenantMemberAccess.projects.map(({ id }) => id)

  const [opened, setOpened] = useState(false)
  const handleClose = useCallback(() => setOpened(false), [])

  const { data: projects } = api.project.names.useQuery()

  const form = useForm<EditMemberFormData>({
    mode: 'all',
    defaultValues: {
      firstName: member.firstName,
      lastName: member.lastName,
      role: member.tenantMemberAccess.role,
      projects: initialProjectIds,
    },
    resolver: zodResolver(EditMemberSchema),
  })

  const selectedRole = form.watch('role')
  const selectedProjects = form.watch('projects')

  const { mutateAsync: editMemberAsync } = api.member.edit.useMutation({
    onSuccess: () => setOpened(false),
  })

  const handleSubmit: SubmitHandler<EditMemberFormData> = useCallback(
    async ({ firstName, lastName, role, projects }) => {
      const dirtyFields = form.formState.dirtyFields
      const projectsChanged =
        projects.length !== initialProjectIds.length || projects.some((id) => !initialProjectIds.includes(id))
      await editMemberAsync({
        id: member.id,
        ...(dirtyFields.firstName && { firstName }),
        ...(dirtyFields.lastName && { lastName }),
        ...(dirtyFields.role && { role }),
        ...(projectsChanged && { projectIds: projects }),
      })
    },
    [editMemberAsync, member.id, initialProjectIds, form.formState.dirtyFields],
  )

  const getCurrentRoleLabel = useCallback(() => {
    return roles.find((role) => role.value === selectedRole)?.label || 'Select Role'
  }, [selectedRole])

  return (
    <Dialog open={opened} onOpenChange={setOpened}>
      <DialogTrigger>
        <GradientBorderButton gradientSize="sm">
          <IconPencil />
        </GradientBorderButton>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <Typography variant="2xl-bold">Edit Member</Typography>
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-6">
          <div className="flex justify-between">
            <Input
              disabled={form.formState.isSubmitting}
              autoComplete="off"
              minLength={1}
              maxLength={32}
              label="Member First Name"
              errorMessage={form.formState.errors.firstName?.message}
              placeholder="Member First Name"
              {...form.register('firstName')}
            />
            <Input
              disabled={form.formState.isSubmitting}
              autoComplete="off"
              minLength={1}
              maxLength={32}
              label="Member Last Name"
              errorMessage={form.formState.errors.lastName?.message}
              placeholder="Member Last Name"
              {...form.register('lastName')}
            />
          </div>

          <Accordion className="flex flex-col gap-6" type="single" collapsible>
            <AccordionItem value="role" className="flex flex-col gap-6">
              <AccordionTrigger
                className="flex min-h-[44px] w-full items-center justify-between rounded-[8px] border border-gray-200 px-4 py-1.5"
                label={getCurrentRoleLabel()}
              ></AccordionTrigger>
              <AccordionContent className="rounded-[8px] border border-gray-300 bg-gray-800 p-4 transition-all">
                <RadioGroup defaultValue={selectedRole} value={selectedRole}>
                  {roles.map(({ label, value }) => (
                    <div
                      key={value}
                      data-selected={selectedRole === value}
                      className="flex h-11 cursor-pointer items-center justify-between rounded-[8px] p-4 data-[selected=true]:bg-gray-900"
                      onClick={() => form.setValue('role', value)}
                    >
                      <Label htmlFor={value} className="text-base text-gray-50">
                        {label}
                      </Label>
                      <RadioGroupItem value={value} id={value} />
                    </div>
                  ))}
                </RadioGroup>
              </AccordionContent>
            </AccordionItem>
            {projects && (
              <AccordionItem value="projects" className="flex flex-col gap-6">
                <AccordionTrigger
                  className="flex min-h-[44px] w-full items-center justify-between rounded-[8px] border border-gray-200 px-4 py-1.5"
                  label="Which projects will be visible?"
                ></AccordionTrigger>
                <AccordionContent className="w-full rounded-[8px] border border-gray-300 bg-gray-800 p-4 transition-all">
                  <div
                    className="h-11 cursor-pointer p-4"
                    onClick={() => {
                      if (selectedProjects.length === projects.length) {
                        form.setValue('projects', [], { shouldValidate: true })
                      } else {
                        form.setValue(
                          'projects',
                          projects.map(({ id }) => id),
                          { shouldValidate: true },
                        )
                      }
                    }}
                  >
                    <div className="pointer-events-none flex items-center justify-between">
                      <Label htmlFor="all-projects" className="w-full text-base text-gray-50">
                        All Projects
                      </Label>
                      <Checkbox id="all-projects" checked={selectedProjects.length === projects.length} />
                    </div>
                  </div>
                  <div className="max-h-60 overflow-y-auto">
                    {projects.map(({ id, name }) => {
                      const checked = selectedProjects.includes(id)

                      const onCheckedChange = () => {
                        if (!checked) {
                          form.setValue('projects', [...selectedProjects, id], { shouldValidate: true })
                        } else {
                          form.setValue(
                            'projects',
                            selectedProjects.filter((projectId) => projectId !== id),
                            { shouldValidate: true },
                          )
                        }
                      }

                      return (
                        <div key={id} className="h-11 cursor-pointer p-4" onClick={onCheckedChange}>
                          <div className="pointer-events-none flex items-center justify-between">
                            <Label htmlFor={id} className="w-full text-base text-gray-50">
                              {name}
                            </Label>
                            <Checkbox id={id} checked={checked} />
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </AccordionContent>
              </AccordionItem>
            )}
          </Accordion>
        </div>
        <DialogFooter className="sm:justify-center sm:space-x-6">
          <SecondaryButton isLoading={form.formState.isSubmitting} onClick={handleClose} className="w-40">
            Cancel
          </SecondaryButton>
          <PrimaryButton
            isLoading={form.formState.isSubmitting}
            onClick={form.handleSubmit(handleSubmit)}
            className="w-40"
          >
            Save
          </PrimaryButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default EditMemberDialog
